/**
 * Page manager section types
 * @module services/pm-section
 */

/**
 * PM section providing list of article content
 * Used by yoga home and is still static content in client code for now
 */
export const SECTION_TYPE_ARTICLE_LIST = 'article-list'

/**
 * PM section providing one or more curated pm-lists of various types
 * Each list is assigned its own label to display as "tabs" in the UI
 * Used by yoga home, member home, centers screen
 */
export const SECTION_TYPE_CONTENT_LIST = 'content-list'

/**
 * PM section providing one or more curated pm-lists of various types
 * Each list is assigned its own label to display as "tabs" in the UI
 * Used by yoga home, member home, centers screen
 */
export const SECTION_TYPE_PROMOTION = 'promotion'

/**
 * PM section providing AI seed question results (3 of them) AND, optionlly,
 * one or more curated content lists of various types (same as PROMOTION section)
 * Each list is assigned its own label to display as "tabs" in the UI
 * Used by yoga home, member home, centers screen
 */
export const SECTION_TYPE_AI_SEED_QUESTIONS = 'ai-seed-questions'

/**
 * PM section providing one or more curated content lists of various types
 * Each list is assigned its own label to display as "tabs" in the UI
 * Used by yoga home, member home, centers screen
 */
export const SECTION_TYPE_CONTENT_REFERENCES_LIST = 'content-references-list'

/**
 * PM section providing combined watch history/continue watching section
 */
export const SECTION_TYPE_CONTINUE_WATCHING = 'continue-watching'

/**
 * PM section providing continue watching section
 */
export const SECTION_TYPE_CONTINUE_WATCHING_ONLY = 'continue-watching-only'

/**
 * PM section providing continue watching v3 section
 */
export const SECTION_TYPE_CONTINUE_WATCHING_V3 = 'continue-watching-v3'

/**
 * PM section providing watch again section
 */
export const SECTION_TYPE_WATCH_AGAIN = 'watch-again'

/**
 * PM section providing an email signup form
 * Used by yoga home, usually only for anonymous
 */
export const SECTION_TYPE_EMAIL_SIGNUP = 'email-signup'

/**
 * PM section providing the recommended "explore by topic" list of topics lists
 */
export const SECTION_TYPE_EXPLORE_BY_TOPIC = 'explore-by-topic'

/**
 * PM section providing a list of series the user has followed
 */
export const SECTION_TYPE_FOLLOWED_SERIES = 'followed-series'

/**
 * Static screen/page header
 * Provides screen title text and "hero"/"jumbotron" style header image
 * Used by yoga home
 */
export const SECTION_TYPE_HEADER = 'header'

/**
 * PM section providing a static link to another section of the app
 * Used by yoga home
 */
export const SECTION_TYPE_LINK = 'link'

/**
 * Legacy list of people with static text and images
 * Used by yoga home
 * @deprecated replaced by content-list section with content-people list
 */
export const SECTION_TYPE_PEOPLE_LIST = 'people-list'

/**
 * Member Home “first touch” overlay module (not spotlight).
 * Section `data` uses `displayMode` (layout), `title`, and `description` (secondary copy).
 * Client apps must read these keys from `data`. Default display mode: {@link MEMBER_HOME_FIRST_TOUCH_DEFAULT_DISPLAY_MODE}.
 */
export const SECTION_TYPE_MEMBER_HOME_FIRST_TOUCH = 'member-home-first-touch'

/**
 * Default `data.displayMode` for {@link SECTION_TYPE_MEMBER_HOME_FIRST_TOUCH} sections.
 * @type {string}
 */
export const MEMBER_HOME_FIRST_TOUCH_DEFAULT_DISPLAY_MODE = 'generic'

/**
 * Allowed `data.displayMode` values for {@link SECTION_TYPE_MEMBER_HOME_FIRST_TOUCH} (admin labels).
 * @type {ReadonlyArray<{ value: string, label: string }>}
 */
export const MEMBER_HOME_FIRST_TOUCH_DISPLAY_MODE_OPTIONS = Object.freeze([
  { value: 'generic', label: 'Generic' },
  { value: 'course', label: 'Course' },
  { value: 'series', label: 'Series' },
  { value: 'ai-focused', label: 'AI Focused' },
  { value: 'ai-plus-generic', label: 'AI + Generic' },
  { value: 'engagement', label: 'Engagement' },
  { value: 'ai-voice-focus', label: 'AI Voice Focus' },
])

/**
 * PM section providing content for an app placement
 * This content typically comes from a recommender or merchandising
 */
export const SECTION_TYPE_PLACEMENT = 'placement'

/**
 * PM section providing a plan selection widget to initiate signup from
 */
export const SECTION_TYPE_PLAN_SELECTION = 'plan-selection'

/**
 * PM section providing the user's playlist
 */
export const SECTION_TYPE_DEFAULT_PLAYLIST = 'playlist'

/**
 * Unknown PM section portals functionality
 */
export const SECTION_TYPE_PORTALS = 'portals'

/**
 * PM Section providing a list of courses
 */
export const SECTION_TYPE_COURSES = 'courses'

/**
 * PM section providing user-specific personalized courses (dynamic row).
 * Data: { header: { label: "<section title>" } }. No content, no listIds.
 * Matches Brooklyn SECTION_TYPE_COURSES_PERSONALIZED.
 */
export const SECTION_TYPE_COURSES_PERSONALIZED = 'courses-personalized'

/**
 * PM section providing a practice picker widget
 * @deprecated this is an incomplete experiment
 */
export const SECTION_TYPE_PRACTICE_PICKER = 'practice-picker'

/**
 * PM section providing a list of recently published content
 */
export const SECTION_TYPE_RECENTLY_ADDED = 'recently-added'

/**
 * PM section providing a list of recommended content for this user
 */
export const SECTION_TYPE_RECOMMENDED_CONTENT = 'recommended-content'

/**
 * PM section providing a list of recommended content for this user
 */
export const SECTION_TYPE_RECOMBEE_RECOMMENDED_CONTENT = 'recombee-recommended-content'

/**
 * PM section providing a list of recommended content for this user
 */
export const SECTION_TYPE_RECOMMENDED_CONTENT_FOR_YOU = 'recommended-content-for-you'

/**
 * PM section providing a list of recommended content for this user
 */
export const SECTION_TYPE_POPULAR_CONTENT_FOR_YOU = 'popular-content-for-you'

/**
 * PM section providing a list of current most popular content
 */
export const SECTION_TYPE_MOST_POPULAR = 'most-popular'

/**
 * PM section providing a list of current most popular content
 */
export const SECTION_TYPE_MOST_POPULAR_SERIES = 'most-popular-series'

/**
 * PM section providing a list of current most popular content
 */
export const SECTION_TYPE_MOST_SHARED = 'top-shared'

/**
 * PM section providing a list of recommended yoga practice content for this user
 */
export const SECTION_TYPE_RECOMMENDED_PRACTICES = 'recommended-practices'

/**
 * Section type for "cuban" recommended series - not currently used
 */
export const SECTION_TYPE_RECOMMENDED_SERIES = 'recommended-series'

/**
 * PM section type for "Because you watched X" recommendations
 */
export const SECTION_TYPE_BECAUSE_YOU_WATCHED = 'because-you-watched'

/**
 * PM section type for "Because you watched X" recommendations
 */
export const SECTION_TYPE_BECAUSE_YOU_WATCHED_RECOMBEE = 'because-you-watched-recombee'

/**
 * PM section providing AI Chat promotion content
 */
export const SECTION_TYPE_AI_CHAT_PROMOTION = 'ai-chat-promotion'

/**
 * PM section type for "Since your entitlement ended"
 */
export const SECTION_TYPE_SINCE_YOUR_ENTITLEMENT_ENDED = 'since-your-entitlement-ended'

/**
 * PM section to display Recombee content
 */
export const SECTION_TYPE_RECOMBEE_CONTENT = 'recombee-content'

/**
 * PM section to display Recombee content with multiple rows
 */
export const SECTION_TYPE_RECOMBEE_MULTI_ROW_CONTENT = 'recombee-multi-row-content'
